/* eslint-disable no-underscore-dangle */
import React from 'react';

import errorReporter from '@app/errorReporter';
import styled from 'styled-components';
import APIService from 'api';
import { ListUserDetailsDTO } from '@bb/domain/dist/types';
import message from '../Message';
import { SearchBar } from '../SearchBar';

import { Input } from '../Input';
import GooglePlacesAutoComplete, { ParsedDetailsResult } from './PlacesService';
import PlacesResult from './PlacesResult';
import { Prediction } from './types';
import PlacesFooter from './PlacesFooter';
import ListingResult from './ListingResult';
import { Icon } from '../Icon';

export { ParsedDetailsResult };

export interface PlacesSearchProps {
  placesType?: 'address' | 'establishment';
  type: 'places';
  onSelect?: (result: ParsedDetailsResult) => void;
}

export interface ListingSearchProps {
  placesType?: undefined;
  type: 'listing';
  onSelect?: (result: ListUserDetailsDTO['listings'][number]) => void;
}

export type AddressSearchProps = (PlacesSearchProps | ListingSearchProps) & {
  autoFocus?: boolean;
  selectMultiple?: boolean;
  input?: string;
  onInputChange?: (value: string) => void;
  customRenderer?: (item: any) => JSX.Element;
  width?: number;
};

interface AddressSearchState {
  ready: boolean;
  searching: boolean;
  results: Prediction[] | ListUserDetailsDTO['listings'];
  manualMode: boolean;
  manualInput: string;
}

class AddressSearch extends React.Component<
  AddressSearchProps,
  AddressSearchState
> {
  private _DEBOUNCE_TIMEOUT = 500;

  private _timeoutId?: NodeJS.Timeout;

  private _GPAInstance: GooglePlacesAutoComplete | undefined;

  private inputRef = React.createRef<HTMLInputElement>();

  public constructor(props: AddressSearchProps) {
    super(props);

    this.state = {
      searching: false,
      ready: false,
      results: [],
      manualMode: !!props.input,
      manualInput: props.input || '',
    };
  }

  private _handleSelectResult = async (result: any) => {
    try {
      const { type } = this.props;

      if (type === 'places') {
        const details = await this._GPAInstance.getPlaceDetails(
          result.place_id,
        );
        const { onSelect } = this.props as PlacesSearchProps;

        onSelect(details);
        return;
      }
    } catch (err) {
      console.error('Could not get details for place');
    }

    const { onSelect } = this.props;
    onSelect(result);
  };

  public handleReady = (ready: boolean) => this.setState({ ready });

  public async componentDidMount() {
    try {
      const { autoFocus, type } = this.props;

      if (autoFocus) {
        setTimeout(() => this.inputRef.current.focus(), 1);
      }

      if (type === 'places') {
        this._initAutocomplete();
      }
      if (type === 'listing') {
        this.handleReady(true);
      }
    } catch (err) {
      errorReporter.error(err);
    }
  }

  private _resultRenderer = (item: any) => {
    const { customRenderer } = this.props;

    if (customRenderer) return customRenderer(item);

    const onClick = () => {
      this._handleSelectResult(item);
    };
    const { type } = this.props;

    if (type === 'places') {
      return <PlacesResult item={item} onClick={onClick} />;
    }
    if (type === 'listing') {
      return <ListingResult item={item} onClick={onClick} />;
    }
    return <></>;
  };

  private _initAutocomplete = () => {
    this._GPAInstance = new GooglePlacesAutoComplete({
      onReady: this.handleReady,
    });
  };

  private _handleSearch = async (input: string) => {
    const { type } = this.props;
    try {
      if (type === 'places') {
        await this._placesSearch(input);
      }
      if (type === 'listing') {
        await this._listingSearch(input);
      }
    } catch (err) {
      message.error('Could not search addresses, please try again.');
      errorReporter.error(err);
      this.setState({ searching: false });
    }
  };

  private _placesSearch = async (input: string) => {
    const { placesType } = this.props;
    if (this._GPAInstance.isReady && input) {
      this.setState({ searching: true });
      const results = await this._GPAInstance.getPlaces(input, placesType);
      this.setState({
        results: results === null ? [] : results,
        searching: false,
      });
    }
  };

  private _listingSearch = async (input: string) => {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }

    this._timeoutId = setTimeout(async () => {
      if (input) {
        this.setState({ searching: true });
        const { listings } = await APIService.GET.getListingsForRole(input);
        this.setState({ results: listings, searching: false });
      }
    }, this._DEBOUNCE_TIMEOUT);
  };

  public setManualMode = (enabled: boolean) =>
    this.setState({ manualMode: enabled, manualInput: '' }, () => {
      const { onInputChange } = this.props;
      if (onInputChange) onInputChange('');
    });

  public setManualInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onInputChange } = this.props;
    const {
      currentTarget: { value },
    } = event;

    this.setState({ manualInput: value }, () => onInputChange(value));
  };

  public render() {
    const { manualInput, manualMode, ready, results, searching } = this.state;

    const { type, width } = this.props;

    return (
      <Container width={width}>
        {manualMode ? (
          <Input
            onChange={this.setManualInput}
            value={manualInput}
            addonAfter={
              <IconContainer onClick={() => this.setManualMode(false)}>
                <Icon type="x" />
              </IconContainer>
            }
            data-cy="address-manual-input"
          />
        ) : (
          <SearchBar
            // @ts-ignore
            ref={this.inputRef}
            allowClear
            onChange={this._handleSearch}
            disabled={!ready}
            placeholder="Search"
            resultData={results}
            customResultRenderer={this._resultRenderer}
            footer={
              type === 'places' ? (
                <PlacesFooter
                  enableManualInput={() => this.setManualMode(true)}
                />
              ) : null
            }
            displayResults
            loading={searching}
          />
        )}
      </Container>
    );
  }
}

export default AddressSearch;

const Container = styled.div<{ width?: number }>`
  ${({ width }) => (width ? `width: ${width}px;` : `width: 100%`)};
`;

const IconContainer = styled.div`
  cursor: pointer;
`;
